import { baseApi } from 'common/store/baseApi';
import {
  shareDocument,
  documentShares,
  documentShare,
  sharedWithMe,
  sharedDocument,
  sharedDocumentByDoc,
  profilePicture,
} from 'common/utils/network/endpoints';
import {
  ShareDocumentRequest,
  ShareDocumentResponse,
  DocumentSharesResponse,
  UpdateShareRequest,
  DocumentShare,
  SharedWithMeResponse,
  SharedWithMeGroupedResponse,
  SharedDocumentResponse,
  UpdateSharedDocumentRequest,
  ProfilePictureResponse,
  UploadProfilePictureResponse,
  ApiResponse,
} from './types';

export const SharingAPI = baseApi
  .enhanceEndpoints({ addTagTypes: ['DocumentShares', 'SharedWithMe', 'SharedDocument', 'ProfilePicture'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Share a document with one or more connections
      shareDocument: builder.mutation<ShareDocumentResponse, { documentId: number; body: ShareDocumentRequest }>({
        query: ({ documentId, body }) => ({
          url: shareDocument.replace('%id%', documentId.toString()),
          method: 'POST',
          body,
        }),
        invalidatesTags: ['DocumentShares'],
      }),

      // Get list of users a document is shared with (cached for 5 minutes)
      getDocumentShares: builder.query<DocumentSharesResponse, number>({
        query: (documentId) => ({
          url: documentShares.replace('%id%', documentId.toString()),
          method: 'GET',
        }),
        providesTags: ['DocumentShares'],
        keepUnusedDataFor: 300, // 5 minutes - includes recipient profile pictures
      }),

      // Update share permissions
      updateSharePermissions: builder.mutation<
        ApiResponse<DocumentShare>,
        { shareId: number; body: UpdateShareRequest }
      >({
        query: ({ shareId, body }) => ({
          url: documentShare.replace('%id%', shareId.toString()),
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['DocumentShares'],
      }),

      // Revoke a share
      revokeShare: builder.mutation<ApiResponse<null>, number>({
        query: (shareId) => ({
          url: documentShare.replace('%id%', shareId.toString()),
          method: 'DELETE',
        }),
        invalidatesTags: ['DocumentShares'],
      }),

      // Get documents shared with me (shorter cache - permissions can change)
      getSharedWithMe: builder.query<SharedWithMeResponse, void>({
        query: () => ({
          url: sharedWithMe,
          method: 'GET',
        }),
        providesTags: ['SharedWithMe'],
        keepUnusedDataFor: 60, // 1 minute - permissions may change, need fresher data
      }),

      // Get shared items grouped by type (vehicles, properties, documents)
      getSharedWithMeGrouped: builder.query<SharedWithMeGroupedResponse, void>({
        query: () => ({
          url: `${sharedWithMe}?grouped=1`,
          method: 'GET',
        }),
        providesTags: ['SharedWithMe'],
        keepUnusedDataFor: 60,
      }),

      // Get shared document details (for explicit shares)
      getSharedDocument: builder.query<SharedDocumentResponse, number>({
        query: (shareId) => ({
          url: sharedDocument.replace('%shareId%', shareId.toString()),
          method: 'GET',
        }),
        providesTags: ['SharedDocument'],
      }),

      // Get shared document details by document ID (for share_all)
      getSharedDocumentByDoc: builder.query<SharedDocumentResponse, number>({
        query: (documentId) => ({
          url: sharedDocumentByDoc.replace('%documentId%', documentId.toString()),
          method: 'GET',
        }),
        providesTags: ['SharedDocument'],
      }),

      // Update shared document (for explicit shares with edit permission)
      updateSharedDocument: builder.mutation<
        SharedDocumentResponse,
        { shareId: number; body: UpdateSharedDocumentRequest }
      >({
        query: ({ shareId, body }) => ({
          url: sharedDocument.replace('%shareId%', shareId.toString()),
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['SharedDocument', 'SharedWithMe'],
      }),

      // Update shared document by document ID (for share_all with edit permission)
      updateSharedDocumentByDoc: builder.mutation<
        SharedDocumentResponse,
        { documentId: number; body: UpdateSharedDocumentRequest }
      >({
        query: ({ documentId, body }) => ({
          url: sharedDocumentByDoc.replace('%documentId%', documentId.toString()),
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['SharedDocument', 'SharedWithMe'],
      }),

      // Get profile picture (cached for 10 minutes)
      getProfilePicture: builder.query<ProfilePictureResponse, void>({
        query: () => ({
          url: profilePicture,
          method: 'GET',
        }),
        providesTags: ['ProfilePicture'],
        keepUnusedDataFor: 600, // 10 minutes - profile pictures rarely change
      }),

      // Upload profile picture
      uploadProfilePicture: builder.mutation<UploadProfilePictureResponse, FormData>({
        query: (formData) => ({
          url: profilePicture,
          method: 'POST',
          body: formData,
        }),
        invalidatesTags: ['ProfilePicture'],
      }),

      // Delete profile picture
      deleteProfilePicture: builder.mutation<ApiResponse<null>, void>({
        query: () => ({
          url: profilePicture,
          method: 'DELETE',
        }),
        invalidatesTags: ['ProfilePicture'],
      }),
    }),
  });

export const {
  useShareDocumentMutation,
  useGetDocumentSharesQuery,
  useUpdateSharePermissionsMutation,
  useRevokeShareMutation,
  useGetSharedWithMeQuery,
  useGetSharedWithMeGroupedQuery,
  useGetSharedDocumentQuery,
  useLazyGetSharedDocumentQuery,
  useGetSharedDocumentByDocQuery,
  useLazyGetSharedDocumentByDocQuery,
  useUpdateSharedDocumentMutation,
  useUpdateSharedDocumentByDocMutation,
  useGetProfilePictureQuery,
  useUploadProfilePictureMutation,
  useDeleteProfilePictureMutation,
} = SharingAPI;

export default SharingAPI;
